import { FC, useMemo } from 'react';

import { Box, Grid, css, styled } from '@mui/material';
import { observer } from 'mobx-react';

import { StyledImmediateAttentionUrgencyContainer } from 'utils/TicketBadgeUtils';

import { ProcessedDepressionData, PHQResponseValue } from 'models/DepressionScreening';

import Icon from 'components/Icons/Icon';

import { Text } from 'components/UIkit/atoms/Text/Text';

type DepressionScreeningViewProps = {
  isExpanded: boolean;
  depressionData: ProcessedDepressionData;
};

// the original requirement was a value of 2
export const COLLAPSED_VIEW_QUESTION_LIMIT = 0;

export const DepressionScreeningView: FC<DepressionScreeningViewProps> = observer(
  ({ depressionData: { hasSuicidalIdeation, responses }, isExpanded }) => {
    const displayResponses = useMemo(
      function getDisplayResponses() {
        if (isExpanded) return responses;

        const collapsedResponses = responses.slice(0, COLLAPSED_VIEW_QUESTION_LIMIT);

        if (hasSuicidalIdeation) {
          const suicidalIdx = responses.findIndex(
            (response) => response.isSuicidalIdeationQuestion
          );

          // suicidal response found and is not within the collapsed set
          if (suicidalIdx >= COLLAPSED_VIEW_QUESTION_LIMIT)
            // Add suicidal response
            collapsedResponses.push(responses[suicidalIdx]);
        }

        return collapsedResponses;
      },
      [isExpanded, responses, hasSuicidalIdeation]
    );

    return (
      <StyledContainer>
        {displayResponses.map((response, index) => (
          <QuestionRow
            index={index}
            key={`${response.questionText}-${index}`}
            questionText={response.questionText}
            responseText={response.responseText}
            score={response.score}
            isSuicidalIdeation={response.isSuicidalIdeationQuestion}
          />
        ))}
      </StyledContainer>
    );
  }
);

type QuestionRowProps = {
  questionText: string;
  responseText: string;
  score: number;
  isSuicidalIdeation?: boolean;
  index: number;
};

const QuestionRow: FC<QuestionRowProps> = ({
  questionText,
  responseText,
  score,
  isSuicidalIdeation,
  index
}) => {
  return (
    <StyledQuestionRow withBorderTop={index === 0}>
      <Grid container spacing={0}>
        <Grid item xs={8.4}>
          {' '}
          {/* 70% width */}
          <StyledQuestionCell>
            <Text variant="body2">{questionText}:</Text>
          </StyledQuestionCell>
        </Grid>
        <Grid item xs={3.6}>
          {' '}
          {/* 30% width */}
          <StyledResponseCell>
            <StyledResponseContainer>
              <Text variant="body2" component="span">
                +{score}
                {'\u00A0' /* space */}
                {'\u00A0' /* space */}
                {responseText}
              </Text>
              {isSuicidalIdeation && score > PHQResponseValue.NotAtAll && (
                <>
                  {' '}
                  <StyledImmediateAttentionUrgencyContainer>
                    <Icon.ImmediateAttention
                      width={16}
                      height={16}
                      data-test-hook="warning-immediate-attention"
                    />
                  </StyledImmediateAttentionUrgencyContainer>
                </>
              )}
            </StyledResponseContainer>
          </StyledResponseCell>
        </Grid>
      </Grid>
    </StyledQuestionRow>
  );
};

const StyledContainer = styled(Box)(
  () => css`
    width: 100%;
  `
);

const StyledQuestionRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'withBorderTop'
})<{ withBorderTop?: boolean }>(
  ({ theme, withBorderTop }) => css`
    width: 100%;
    border-bottom: 1px solid ${theme.palette.natural.border};
    border-top: ${withBorderTop ? '1px solid ' + theme.palette.natural.border : 'none'};
  `
);

const StyledQuestionCell = styled(Box)(
  ({ theme }) => css`
    padding: ${theme.spacing(8, 8, 8, 0)};
  `
);

const StyledResponseCell = styled(Box)(
  ({ theme }) => css`
    padding: ${theme.spacing(8, 0)};
  `
);

const StyledResponseContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(8)};
  `
);
